import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Context } from "../../../states/Store";
import {
  InitialThirdpartiesTableSort,
  getThirdpartiesTableColumns,
  thirdpartiesDefaultVisibilityColumns,
} from "./ThirdpartiesTableUtils";
import { useHistory } from "react-router";
import { ERROR, Exports, FieldType, SUCCESS } from "../../../services/common/Constants";
import i18next from "i18next";
import {getAccountThirdparties, getExportFileFromAccountThirdParties} from "../../../services/edge/ThirdpartyService";
import EADataGrid from "../../../components/common/datatable/EADataGrid";
import {
  formatActivityText,
  formatActivityTextFromActivity,
  formatTurnoverAndCurrency,
  formatEsgDepthValues,
  formatEsgScoresValues,
  formatFinancialStrengthRiskLevel,
  formatRegistrationStatus,
  formatThirdpartyIdentifier,
  getActivityOriginFromType,
  getCountryName,
} from '../../../services/common/Utils';
import {
  buildAttributesGridColumns,
  formatAttributeValues,
} from "../../../services/common/AttributeUtils";
import {
  formatCalculationResult,
  buildGridColumnsForCalculations,
  CalculationTargetResourceType,
} from "../../../services/common/CalculatorUtils";
import { ThirdpartyAddButton } from "../../../components/common/datatable/DataTableButtons";
import ROUTES from "../../../config/Routes";
import ListLayout from "../../../components/common/ListLayout";
import { CountSelectedRow } from "components/common/datatable/CountSelectedRow";
import { ThirdpartiesSelectionMenu } from "./ThirdpartiesSelectionMenu";
import { formatInduedRiskLevel } from "services/common/InduedUtils";
import { PrivilegeEnum, RoleBasedAccessControlContext } from "services/common/RolesUtils";
import { ThirdpartiesListSearchForm } from "./ThirdpartiesListSearchForm/ThirdpartiesListSearchForm";
import { useRef } from "react";
import { getApiErrorMessageCode, isAbortError } from 'services/common/ApiUtils';
import { useListFormFilters } from "components/common/hooks/useListFormFilters";
import { useColumnsVisibility } from "components/common/hooks/useColumnsVisibility";
import { isNoEsgScoring } from "services/common/DataBlocksUtils";
import { EvAllScores } from "services/common/EcovadisUtils";
import ThirdpartiesPanel from '../../../components/thirdparties/sheet/ThirdpartiesPanel';
import { getAccountCalculations } from "services/edge/CalculatorService";
import { isEmpty } from "lodash";
import { getAccountAttributesByBondType, getAccountCategorizedAttributes } from "services/edge/AttributesService";
import { SecurityRatingAllScoresEnum } from "services/common/SecurityRatingUtils";
import { Box } from '@mui/system';
import EATooltip from '../../../components/common/EATooltip';
import { CreditsafeIndicators } from 'services/common/CreditsafeUtils';
import { getAccountConsumption } from '../../../services/edge/ConsumptionService';

function ThirdpartiesDataTable({ active }) {
  const [state, dispatch] = useContext(Context);
  const [thirdparties, setThirdparties] = useState();
  const [shownThirdparties, setShownThirdparties] = useState([]);
  const [loader, setLoader] = useState(true);
  const [attributesColumns, setAttributesColumns] = useState();
  const [categorizedAttributesColumns, setCategorizedAttributesColumns] = useState();
  const [calculatedAttributesColumns, setCalculatedAttributesColumns] = useState();
  const [indicatorsColumns, setIndicatorsColumns] = useState();
  const [thirdpartiesColumns, setThirdpartiesColumns] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [thirdpartiesSelected, setThirdpartiesSelected] = useState([]);
  const [showTransparency, setShowTransparency] = useState(false);
  const [globalCount, setGlobalCount] = useState(0);
  const { hasAnyOfPrivileges, hasAnyOfPrivilegesOnPropertyId } = useContext(RoleBasedAccessControlContext);
  const [isThirdpartiesPanelOpen, setIsThirdpartiesPanelOpen] = useState(false);
  const [thirdpartySelectedId, setThirdpartySelectedId] = React.useState()

  const [attributes, setAttributes] = useState();
  const [categorizedAttributes, setCategorizedAttributes] = useState();
  const [calculatedAttributes, setCalculatedAttributes] = useState();
  const [indicators, setIndicators] = useState();
  // eslint-disable-next-line
  const [consumption, setConsumption] = useState();
  const [disableAddThirdpartyButton, setDisableAddThirdpartyButton] = useState(false);

  const abortControllerRef = useRef();

  const history = useHistory();
  const [sortModel, setSortModel] = useState(
    InitialThirdpartiesTableSort
  );

  const openPanel = (thirdpartyId) => {
    setThirdpartySelectedId(thirdpartyId);
    setIsThirdpartiesPanelOpen(true);
  };

  const {
    formFilters,
    saveSessionFormFilters
  } = useListFormFilters("thirdparties-list");

  const {
    columnsVisibility,
    setColumnsVisibility,
    setInitialColumnsVisibility
  } = useColumnsVisibility("thirdparties-list", true);

  const fetchThirdparties = useCallback((page, rowPerPage, filters, sortModel) => {
    if(abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    setLoader(true);
    (async function fetchData() {
      try {
        const resp = await getAccountThirdparties(state.account.id, active, { page, rowPerPage }, filters, sortModel, null, signal);
        setThirdparties(resp.content || []);
        dispatch({
          type: "PAGINATION", pagination: {
            page: resp.page,
            rowPerPage: resp.size,
            count: resp.totalElements
          }
        });
        setGlobalCount(resp.globalCount || resp.totalElements)
        setIsLoading(false);
        setLoader(false);
      } catch (e) {
        if (!isAbortError(e)) {
          dispatch({ type: "ALERT", alert: { type: ERROR, msg: e.message } });
          setThirdparties([]);
        }
        setLoader(false);
      }
    })();
  }, [state.account, active, dispatch])

  // -------- Data ------- //
  useEffect(() => {
    if (state.account && formFilters) {
      fetchThirdparties(state.pagination.page, state.pagination.rowPerPage, formFilters, sortModel)
    }
    return () => {
      abortControllerRef.current?.abort();
    }
  }, [state.account, fetchThirdparties, state.pagination.page, state.pagination.rowPerPage, formFilters, sortModel]);

  // Map thirdparties
  useEffect(() => {
    let mappedTps = thirdparties?.map((tp) => {
      const activityOrigin = getActivityOriginFromType(tp.etabActivityType)
      const activityTranslated = state.activityMapByOrigin.get(activityOrigin)?.get(tp.etabActivityCode)
      const activityMajorCode = activityTranslated?.majorCode ?? tp.majorCode
      return {
        ...tp,
        id: tp.thirdpartyId,
        identifier: formatThirdpartyIdentifier(tp),
        duns: !tp.dunsEncrypted ? tp.duns : "",
        vat: tp.tva,
        activity:  activityTranslated ? formatActivityTextFromActivity(activityTranslated) : formatActivityText(
          tp.etabActivityType,
          tp.etabActivityCode,
          tp.etabActivityLabel
        ),
        mainActivity: activityMajorCode ? i18next.t(`majorActivities.${activityMajorCode}`) : tp.majorEn || tp.majorFr || "",
        headquarter: tp.headquarter ? "headquarters" : "secondaryBranch",
        thirdpartyAlerts: !(
          tp.thirdpartyAlerts === "NEANT" || !tp.thirdpartyAlerts
        ),
        addressCountry: getCountryName(
          tp.addressCountryCode,
          i18next.language
        ),
        turnover: formatTurnoverAndCurrency(tp),
        registered: formatRegistrationStatus(tp.registrationStatus),
        hasRestrictedDisclosure: tp.hasRestrictedDisclosure,
        complete: tp.complete ? "complete" : "incomplete",
        indued: formatInduedRiskLevel(tp.scores?.indued),
        financialStrengthScore: formatFinancialStrengthRiskLevel(tp.scores?.datablocks_financial_strength?.riskLevel),
        financialStrengthRating: tp.scores?.datablocks_financial_strength?.riskLevel ? (tp.scores?.datablocks_financial_strength?.rating || "") : i18next.t(`financialStrength.scoreNotActivated`),
        esgScore: formatEsgScoresValues(tp.scores?.datablocks_esg?.score, isNoEsgScoring(tp.scores?.datablocks_esg)),
        esgDepth: formatEsgDepthValues(tp.scores?.datablocks_esg?.esgDataDepth, isNoEsgScoring(tp.scores?.datablocks_esg)),
        esgRiskEnvironment: formatEsgScoresValues(tp.scores?.datablocks_esg?.environmentalRankingScore, isNoEsgScoring(tp.scores?.datablocks_esg)),
        esgRiskSocial: formatEsgScoresValues(tp.scores?.datablocks_esg?.socialRankingScore, isNoEsgScoring(tp.scores?.datablocks_esg)),
        esgRiskGovernance: formatEsgScoresValues(tp.scores?.datablocks_esg?.governanceRankingScore, isNoEsgScoring(tp.scores?.datablocks_esg)),
        ...EvAllScores?.reduce(
          (res, score) => {
            const scopeScoreKey = `scope_${score}_performance`;
            const scorePerformance = tp.scores?.ecovadis?.ev_data?.[scopeScoreKey]?.toLowerCase() || "not_started";
            return {
              ...res,
              [`ecovadis_${score}`]: scorePerformance,
            }
          },
          {}),
        ...Object.values(SecurityRatingAllScoresEnum)?.reduce(
          (res, score) => {
            let scorePerformance;
            if(score === SecurityRatingAllScoresEnum.GLOBAL_SCORE) {
              scorePerformance = tp.scores?.security_rating?.score?.maturity || "not_started";
            } else {
              scorePerformance = tp.scores?.security_rating?.[`${score}_score`]?.letter_value || "not_started";
            }
            return {
              ...res,
              [`security_rating_${score}`]: scorePerformance,
            }
          },
          {}),
          ...Object.values(CreditsafeIndicators)?.reduce(
          (res, score) => {
            const scorePerformance = tp.scores?.creditsafe?.[score] || "not_started";
            return {
              ...res,
              [`creditsafe_${score}`]: scorePerformance,
            }
          },
          {}),
        ...tp.attributeValues?.reduce(
          (res, attr) => ({
            ...res,
            [`attribute-${attr.label}`]: formatAttributeValues(attr),
          }),
          {}
        ),
        ...tp.indicators?.reduce(
          (res, indic) => ({
            ...res,
            [`indicator-${indic.name}`]: formatCalculationResult(indic),
          }),
          {}
        ),
        ...tp.calculatedAttributes?.reduce(
          (res, attr) => ({
            ...res,
            [`calculatedAttribute-${attr.name}`]: formatCalculationResult(attr),
          }),
          {}
        ),
        authorizingRoles: tp.authorizingRoles,
      };
    });
    setShownThirdparties(mappedTps || []);
  }, [thirdparties, hasAnyOfPrivileges, state.activityMapByOrigin]);


  // Fetch calculations
  useEffect(() => {
    if (state.account) {
      (async function fetchData() {
        try {
          const res = await getAccountCalculations(state.account.id, CalculationTargetResourceType.THIRD_PARTY, dispatch);
          
          setCalculatedAttributes(
            res.content?.filter(
              (x) => x.nature === FieldType.CALCULATED_ATTRIBUTE
            )
          );

          setIndicators(
            res.content?.filter((x) => x.nature === FieldType.INDICATOR)
          );
        } catch (e) {
          dispatch({ type: "ALERT", alert: { type: ERROR, msg: e.message } });
          setCalculatedAttributes([]);
          setIndicators([]);
        }
      })();
    }
  }, [state.account, dispatch]);


  // Fetch Account Thirdparties Attributes
  useEffect(() => {
    if (state.account) {
      (async function fetchThirdpartiesAttributes() {
        try {
          const res = await getAccountAttributesByBondType(
            state.account.id,
            "thirdparty",
            dispatch
          );
          setAttributes(res.content);
        } catch (e) {
          dispatch({ type: "ALERT", alert: { type: ERROR, msg: e.message } });
          setAttributes([]);
        }
      })();
    }
  }, [state.account, dispatch]);
  
  // Fetch Account Categorized Thirdparties Attributes
  useEffect(() => {
    if (state.account) {
      (async function fetchCategorizedThirdpartiesAttributes() {
        try {
          const res = await getAccountCategorizedAttributes(
            state.account.id,
            null,
            "thirdparty",
            dispatch
          );
          setCategorizedAttributes(res.content);
        } catch (e) {
          dispatch({ type: "ALERT", alert: { type: ERROR, msg: e.message } });
          setCategorizedAttributes([]);
        }
      })();
    }
  }, [state.account, dispatch]);


  useEffect(() => {
    if (state.account) {
      (async function fetchData() {
        try {
          const resp = await getAccountConsumption(state.account.id);
          setConsumption(resp.content);
        } catch (e) {
          dispatch({ type: 'ALERT', alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
        }
      })();
    }
  }, [state.account, dispatch]);


  // Init thirdparties attributes/calculations columns
  useEffect(() => {
    if (thirdparties && attributes && calculatedAttributes && indicators && categorizedAttributes) {

      if (!isEmpty(attributes) && hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE)) {
        setAttributesColumns(buildAttributesGridColumns(attributes?.filter(attr => hasAnyOfPrivilegesOnPropertyId(attr.id, PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE))));
      } else {
        setAttributesColumns([]);
      }
      
      if (!isEmpty(categorizedAttributes) && hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE)) {
        setCategorizedAttributesColumns(buildAttributesGridColumns(categorizedAttributes?.filter(attr => hasAnyOfPrivilegesOnPropertyId(attr.id, PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE))));
      } else {
        setCategorizedAttributesColumns([]);
      }

      if (!isEmpty(calculatedAttributes)) {
        setCalculatedAttributesColumns(buildGridColumnsForCalculations(calculatedAttributes?.filter(attr =>
          hasAnyOfPrivilegesOnPropertyId(attr.id, PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE))))
      } else {
        setCalculatedAttributesColumns([]);
      }

      if (!isEmpty(indicators)) {
        setIndicatorsColumns(buildGridColumnsForCalculations(indicators.filter(cal =>
          hasAnyOfPrivilegesOnPropertyId(cal.id, PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_INDICATOR))))
      } else {
        setIndicatorsColumns([]);
      }

      if (thirdparties?.some(tp => tp.transparencyScore !== null)) {
        setShowTransparency(true);
      }
    }
  }, [thirdparties, attributes, categorizedAttributes, calculatedAttributes, indicators, hasAnyOfPrivileges, hasAnyOfPrivilegesOnPropertyId])

  // Set Columns Default visibility
  useEffect(() => {
    if (attributesColumns || categorizedAttributesColumns || calculatedAttributesColumns || indicatorsColumns) {
      setInitialColumnsVisibility(thirdpartiesDefaultVisibilityColumns(attributesColumns, categorizedAttributesColumns, calculatedAttributesColumns, indicatorsColumns, active));
    }
  }, [active, attributesColumns, categorizedAttributesColumns, calculatedAttributesColumns, indicatorsColumns, setInitialColumnsVisibility]);

  // Set Grid Columns
  useEffect(() => {
    if (attributesColumns && categorizedAttributesColumns && calculatedAttributesColumns && indicatorsColumns) {
      setThirdpartiesColumns(
        getThirdpartiesTableColumns(
          attributesColumns,
          categorizedAttributesColumns,
          calculatedAttributesColumns,
          indicatorsColumns,
          active,
          history,
          state.account.id,
          showTransparency,
          hasAnyOfPrivileges,
          state.accounts,
          openPanel,
          sortModel,
        )
      );
    }
  }, [
    attributesColumns,
    categorizedAttributesColumns,
    calculatedAttributesColumns,
    indicatorsColumns,
    active,
    history,
    state.account,
    showTransparency,
    hasAnyOfPrivileges,
    state.accounts,
    sortModel
  ]);

  useEffect(() => {
    if(state.account && consumption) {
      setDisableAddThirdpartyButton(consumption && consumption.isThirdPartyOnboardingPossible === false)
    }
  }, [state.account, consumption, setDisableAddThirdpartyButton]);

  const addThirdparty = () => {
    history.push(`/${state.account.id}/${ROUTES.THIRDPARTY_ADD}`);
  };

  const handleSearch = (data) => {
    saveSessionFormFilters(data);
    dispatch({ type: "PAGINATION", pagination: {
      page: 0,
      rowPerPage: state.pagination.rowPerPage
    }});
    setThirdpartiesSelected([]);
  }

  const handleSortModelChange = (newSortModel) => {
    if (newSortModel[0] !== sortModel[0]) {
      setSortModel(newSortModel);
      dispatch({
        type: "PAGINATION", pagination: {
          page: 0,
          rowPerPage: state.pagination.rowPerPage
        }
      });
    }
  }

  const externalExportHandler = async (exportType) => {
    let objetResult = null;
    dispatch({
      type: "ALERT",
      alert: { type: SUCCESS, msg: i18next.t(`tableLabels.toolbar.downloadLoading`) },
    });
    const exportDto = {
      columnsVisibility: {
        ...columnsVisibility,
        stoppingDate: !active && columnsVisibility.stoppingDate
      },
      thirdpartiesSelected: thirdpartiesSelected
    }
    switch (exportType) {
      case Exports.CSV :
      case Exports.XLS :
      case Exports.XLSX :
        objetResult = await getExportFileFromAccountThirdParties(state.account.id, active, exportType, formFilters, sortModel, exportDto, dispatch)
        break;
      default: console.log("Type d'export tiers non géré");
        break;
    }

    if (objetResult !== null) {
      const a = document.createElement('a')
      a.href = URL.createObjectURL(new Blob([objetResult.arrayBuffer]))
      a.download = objetResult.filename
      a.click()
    }
  }

  // Reset pagination on component unmount
  useEffect(() => {
    return () => {
      dispatch({
        type: "PAGINATION",
        pagination: {
          page: 0,
          rowPerPage: 50,
          count: 0,
        }
      });
    };
  }, [dispatch]);

  return (
    <ListLayout
      count={state.pagination?.count}
      totalCount={globalCount}
      icon={active ? 'drawer/thirdparty_list' : 'drawer/archive_box_list'}
      title={i18next.t(
        `thirdpartiesTable.title.${active ? 'active' : 'inactive'}`
      )}
      addingComponent={active &&
        <EATooltip title={disableAddThirdpartyButton ? i18next.t("addThirdparty.thirdpartyThresholdReached", { threshold: consumption?.thirdPartyThreshold}) : ""}
                   arrow
                   placement={'left'}
        >
          <Box>
            <ThirdpartyAddButton
              onClickHandler={addThirdparty}
              disabled={disableAddThirdpartyButton}
            />
        </Box>
        </EATooltip>
      }
      isLoading={isLoading}
      filterField={
        formFilters && (
          <ThirdpartiesListSearchForm
            filters={formFilters}
            attributes={[
              ...(attributes?.filter(attr => hasAnyOfPrivilegesOnPropertyId(attr.id, PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE)) || []),
              ...(categorizedAttributes?.filter(attr => hasAnyOfPrivilegesOnPropertyId(attr.id, PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE)) || [])
            ]}
            calculatedAttributes={calculatedAttributes?.filter(cal => hasAnyOfPrivilegesOnPropertyId(cal.id, PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_CALCULATED_ATTRIBUTE))}
            indicators={indicators?.filter(cal => hasAnyOfPrivilegesOnPropertyId(cal.id, PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_INDICATOR))}
            isLoading={loader}
            submitHandler={handleSearch}
          />
        )
      }
    >
      {/* DataGrid list */}
      {shownThirdparties && thirdpartiesColumns && columnsVisibility && (
        <EADataGrid
          loading={loader}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          additionnalsLeftComponents={
            <>
              <CountSelectedRow data={thirdpartiesSelected} />
              <ThirdpartiesSelectionMenu
                thirdpartiesSelected={thirdpartiesSelected}
              />
            </>
          }
          selection={{
            items: thirdpartiesSelected,
            setItems: setThirdpartiesSelected,
          }}
          initialVisibilityModel={columnsVisibility}
          updateVisibilityModel={setColumnsVisibility}
          hasFilters={false}
          inputRows={shownThirdparties}
          columns={thirdpartiesColumns}
          hasCustomPagination
          paginationMode="server"
          rowCount={state.pagination?.count || 0}
          externalExportHandler={externalExportHandler}
        />
      )}

      {isThirdpartiesPanelOpen && (
        <ThirdpartiesPanel
          isOpen={isThirdpartiesPanelOpen}
          thirdparties={thirdparties}
          isLoading={loader}
          thirdpartySelectedId={thirdpartySelectedId}
          handleClose={() => {
            setIsThirdpartiesPanelOpen(false);
            setThirdpartySelectedId(null);
          }}
          accountId={state.account.id}
          active={active}
        />
      )}
    </ListLayout>
  );
}
export default ThirdpartiesDataTable;
